const env = import.meta.env

// SSR (www prerender) support: in Node there is no window, so WWW_SSR_ORIGIN supplies the
// origin that window.location provides in the browser (e.g. "http://localhost:3100" in
// development). In production builds the VITE_BASE_* vars are baked in, so the fallback is
// only exercised in environments where they are unset.
const nodeEnv: Record<string, string | undefined> =
    (globalThis as {process?: {env?: Record<string, string | undefined>}}).process?.env ?? {}
const isBrowser = typeof window !== 'undefined'
const ssrOriginUrl: URL | null = !isBrowser && nodeEnv.WWW_SSR_ORIGIN ? new URL(nodeEnv.WWW_SSR_ORIGIN) : null
const locationHost: string = isBrowser ? window.location.host : (ssrOriginUrl?.host ?? '')
const locationHostname: string = isBrowser ? window.location.hostname : (ssrOriginUrl?.hostname ?? '')
const locationProtocol: string = isBrowser ? window.location.protocol : (ssrOriginUrl?.protocol ?? 'https:')

const appDomain: string = env.VITE_BASE_APP_DOMAIN || locationHost
const appPath: string = env.VITE_BASE_APP_PATH || '/'
const configuredApiDomain: string = env.VITE_BASE_API_DOMAIN || appDomain
const configuredApiPath: string = env.VITE_BASE_API_PATH || (configuredApiDomain == appDomain ? '/api/' : '/')
const configuredInternalAdminDomain: string = env.VITE_BASE_INTERNAL_ADMIN_DOMAIN || appDomain
const configuredInternalAdminPath: string = env.VITE_BASE_INTERNAL_ADMIN_PATH || (configuredInternalAdminDomain == appDomain ? '/internal_admin/' : '/')
const isLocalHost = locationHostname === 'localhost' || locationHostname === '127.0.0.1'
const shouldOverrideToLocal = isLocalHost && configuredApiDomain.includes('ngrok') && configuredApiPath.endsWith('/api/')
const apiDomain: string = shouldOverrideToLocal ? locationHost : configuredApiDomain
const apiPath: string = shouldOverrideToLocal ? '/api/' : configuredApiPath
const internalAdminDomain: string = shouldOverrideToLocal ? locationHost : configuredInternalAdminDomain
const internalAdminPath: string = shouldOverrideToLocal ? '/internal_admin/' : configuredInternalAdminPath
const wwwDomain: string = env.VITE_BASE_WWW_DOMAIN || appDomain
const wwwPath: string = env.VITE_BASE_WWW_PATH || (wwwDomain == appDomain ? '/www/' : '/')
const partnersDomain: string = env.VITE_BASE_PARTNERS_DOMAIN || appDomain
const partnersPath: string = env.VITE_BASE_PARTNERS_PATH || (partnersDomain == appDomain ? '/partners/' : '/')

function buildUrl(domain: string, basePath: string, path: string, params: Record<string, string | number | boolean | null | undefined> = {}): string {
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return new URL(path).toString()
    }

    const protocol = (domain.includes('localhost') || domain.includes('127.0.0.1')) ? locationProtocol : 'https:'
    const normalizedBase = basePath.endsWith('/') ? basePath : `${basePath}/`
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path
    const url = new URL(`${normalizedBase}${normalizedPath}`, `${protocol}//${domain}`)

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            url.searchParams.set(key, value.toString())
        }
    })

    return url.toString();
}

export const appUrlTo = (path: string, params: Record<string, string | number | boolean | null | undefined> = {}): string => buildUrl(appDomain, appPath, path, params)
export const wwwUrlTo = (path: string, params: Record<string, string | number | boolean | null | undefined> = {}): string => buildUrl(wwwDomain, wwwPath, path, params)
export const apiUrlTo = (path: string, params: Record<string, string | number | boolean | null | undefined> = {}): string => buildUrl(apiDomain, apiPath, path, params)
export const internalAdminUrlTo = (path: string, params: Record<string, string | number | boolean | null | undefined> = {}): string => buildUrl(internalAdminDomain, internalAdminPath, path, params)
export const partnersUrlTo = (path: string, params: Record<string, string | number | boolean | null | undefined> = {}): string => buildUrl(partnersDomain, partnersPath, path, params)

// Example configuration in a development environment using ngrok where each service is exposed on a different subdomain:
// export VITE_BASE_APP_DOMAIN=jay-o.ngrok.io
// export VITE_BASE_APP_PATH=/
// export VITE_BASE_API_DOMAIN=jay-o-api.ngrok.io
// export VITE_BASE_API_PATH=/
// export VITE_BASE_PARTNERS_DOMAIN=jay-o-partners.ngrok.io
// export VITE_BASE_PARTNERS_PATH=/
// export VITE_BASE_WWW_DOMAIN=jay-o-www.ngrok.io
// export VITE_BASE_WWW_PATH=/

// Example configuration in a development/test environment using ngrok where each service is exposed on the same domain, but under a subdirectory.
// export VITE_BASE_APP_DOMAIN=localhost:3000
// export VITE_BASE_APP_PATH=/
// export VITE_BASE_API_DOMAIN=localhost:3000
// export VITE_BASE_API_PATH=/api/
// export VITE_BASE_PARTNERS_DOMAIN=localhost:3000
// export VITE_BASE_PARTNERS_PATH=/partners/
// export VITE_BASE_WWW_DOMAIN=localhost:3000
// export VITE_BASE_WWW_PATH=/www/

// This is how staging is set up:
// export VITE_BASE_APP_DOMAIN=staging-app.osello.com
// export VITE_BASE_APP_PATH=/
// export VITE_BASE_API_DOMAIN=staging-api.osello.com
// export VITE_BASE_API_PATH=/
// export VITE_BASE_PARTNERS_DOMAIN=staging-partners.osello.com
// export VITE_BASE_PARTNERS_PATH=/
// export VITE_BASE_WWW_DOMAIN=staging-www.osello.com
// export VITE_BASE_WWW_PATH=/

// This is how production is set up:
// export VITE_BASE_APP_DOMAIN=app.osello.com
// export VITE_BASE_APP_PATH=/
// export VITE_BASE_API_DOMAIN=api.osello.com
// export VITE_BASE_API_PATH=/
// export VITE_BASE_PARTNERS_DOMAIN=partners.osello.com
// export VITE_BASE_PARTNERS_PATH=/
// export VITE_BASE_WWW_DOMAIN=www.osello.com
// export VITE_BASE_WWW_PATH=/
